import React, { useState, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";

import {
  DescriptionCard,
  DraggableCard,
  PenPortraitCard,
  TopMindsetCard,
  TopBrandsCard,
} from "@/components/atoms";
import { DemographicsSection, PenPortraitMap } from "@/components/molecules";
import { useInsightFilters } from "@/context";
import {
  AudienceSegment,
  InsightMeta,
  InsightResult,
  LocationResult,
  PenPortrait,
  PenPortraitInsightVariable,
  PenPortraitSection,
} from "@/types";
import { toIconLabel } from "@/utils";

interface MindsetPenPortraitViewProps {
  mindset: AudienceSegment;
  meta: InsightMeta;
  insights: InsightResult[];
  pen_portrait: PenPortrait;
  locations: LocationResult;
  editable?: boolean;
  mindsets?: PenPortraitInsightVariable[];
}

export const MindsetPenPortraitView: React.FC<MindsetPenPortraitViewProps> = ({
  mindset,
  meta,
  insights,
  pen_portrait,
  locations,
  editable = true,
  mindsets,
}) => {
  const {
    filters: { scoreType, levels },
  } = useInsightFilters();

  const [sections, setSections] = useState<PenPortraitSection[]>([]);
  const src = `https://sc-opendata.s3.eu-west-1.amazonaws.com/mindset_images/${toIconLabel(mindset.segment_name)}.png`

  useEffect(() => {
    // Add mindset section to the start
    if (mindsets) {
      setSections([ ({title: 'mindset', id: 99, score: 0}), ...pen_portrait.sections ])
    } else {
      setSections([ ...pen_portrait.sections ])
    }
  }, [pen_portrait, mindsets])

  const handleMoveCard = (dragSection: string, hoverSection: string, dragIndex: number, hoverIndex: number) => {
    const updatedSections = [...sections];
    // Swap the card positions
    [updatedSections[hoverIndex], updatedSections[dragIndex]] = [
      updatedSections[dragIndex],
      updatedSections[hoverIndex],
    ];
    setSections(updatedSections);
  };

  const handleRemoveSection = (index: number) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
  };

  return (
    <Grid container size={12} spacing={2}>
      <Grid container size={12} justifyContent="space-between">

        <Grid container size={8} alignContent="space-around">
          <Stack direction="row" spacing={2}>
            <Box
              component="img"
              src={src}
              alt={mindset.segment_name}
              maxWidth={300}
              height={200}
              sx={{ borderRadius: 2, objectFit: "cover" }}
            />
            <TopBrandsCard
              variables={pen_portrait.top_scores}
              score={scoreType.value} levels={levels}
            />
          </Stack>

          <DemographicsSection
            insights={insights}
            score={scoreType.value}
          />
        </Grid>

        <Grid size={4}>
          <PenPortraitMap scores={locations} extra={false}/>
        </Grid>

      </Grid>

      <Grid size={12}>
        <DescriptionCard description={meta.description_short} editable={editable}/>
      </Grid>

      <DndProvider backend={HTML5Backend}>
        {sections.map((section, index) => {
          if (section.title === 'mindset' && mindsets) {
            return (
              <Grid size={4} key={`pen-portrait-mindsets`}>
                <DraggableCard
                  section="top"
                  index={index}
                  onMoveCard={handleMoveCard}
                  type="card"
                >
                  <TopMindsetCard
                    title={'What are their mindsets?'}
                    variables={mindsets}
                    score={scoreType.value}
                    onRemoveSection={() => handleRemoveSection(index)}
                    editable={editable}
                  />
                </DraggableCard>
              </Grid>
            );
          } else {
            const variables = pen_portrait.insight.find(
              (v) => v.section === section.title
            )?.variables;
            if (variables) {
              return (
                <Grid size={4} key={`pen-portrait-${index}`}>
                  <DraggableCard
                    section="top"
                    index={index}
                    onMoveCard={handleMoveCard}
                    type="card"
                  >
                    <PenPortraitCard
                      title={section.title}
                      variables={variables}
                      score={scoreType.value}
                      levels={levels}
                      onRemoveSection={() => handleRemoveSection(index)}
                      editable={editable}
                    />
                  </DraggableCard>
                </Grid>
              );
            } else {
              return null
            }
          }
        })}
      </DndProvider>
    </Grid>
  );
};

export default MindsetPenPortraitView;
